import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@/contexts/AuthContext';
import { VacationRequestCard } from './components/VacationRequestCard';
import { VacationRequestDetailsModal } from '../VacationRequestDetailsModal';
import { useVacationRequestMutations } from '../../hooks/data';
import { useDraftAutoOpen } from '../../hooks/useDraftAutoOpen';
import { resolveReservedDays } from '../../utils/vacationBalanceDisplay';
import type { VacationRequest } from '../../models/vacation.types';
import type { VacationRequestListProps } from './vacationRequestList.types';

export function VacationRequestList({ requests, isLoading, receiptsByRequestId }: VacationRequestListProps) {
    const { user } = useAuth();
    const { deleteRequest, resendClientEmail, confirmRequest, submitCorrection } = useVacationRequestMutations();
    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedId, setSelectedId] = useState<string | null>(null);

    useEffect(() => {
        const requestId = searchParams.get('requestId');
        if (requestId) {
            setSelectedId(requestId);
        }
    }, [searchParams]);

    const handleAutoOpen = useCallback(
        (request: VacationRequest) => {
            confirmRequest?.mutate({
                requestId: request.id,
                collaboratorEmail: request.collaboratorEmail,
                collaboratorId: request.collaboratorId,
                silent: true,
            });
        },
        [confirmRequest]
    );

    useDraftAutoOpen({
        requests,
        currentUserEmail: user?.email,
        onOpen: handleAutoOpen,
    });

    const handleSelectRequest = (id: string) => {
        setSelectedId(id);
        setSearchParams({ requestId: id }, { replace: true });
    };

    const handleCloseModal = () => {
        setSelectedId(null);
        setSearchParams({}, { replace: true });
    };

    if (isLoading) {
        return (
            <div className="space-y-3" aria-busy="true">
                {[0, 1].map((index) => (
                    <div key={index} className="h-24 animate-pulse rounded-xl bg-gray-100" />
                ))}
            </div>
        );
    }

    if (requests.length === 0) {
        return (
            <div className="rounded-xl border border-dashed border-primary-200 p-8 text-center">
                <CalendarDaysIcon
                    className="mx-auto h-10 w-10 text-raro-gray-300"
                    aria-hidden="true"
                />
                <p className="mt-2 text-sm font-medium text-raro-gray-700 font-montserrat">
                    Nenhuma solicitação registrada
                </p>
                <p className="mt-1 text-sm text-raro-gray-500 font-montserrat">
                    Use o formulário acima para solicitar suas férias.
                </p>
            </div>
        );
    }

    const selected = requests.find((request) => request.id === selectedId) ?? null;

    return (
        <>
            <ul className="space-y-3">
                {requests.map((request) => (
                    <VacationRequestCard
                        key={request.id}
                        request={request}
                        onSelect={() => handleSelectRequest(request.id)}
                        isConfirmingRequest={
                            confirmRequest?.isPending ? confirmRequest.variables?.requestId === request.id : false
                        }
                        onConfirmRequest={(requestId) => {
                            confirmRequest?.mutate({
                                requestId,
                                collaboratorEmail: request.collaboratorEmail,
                                collaboratorId: request.collaboratorId,
                            });
                        }}
                        receipts={receiptsByRequestId?.get(request.id)}
                    />
                ))}
            </ul>

            <VacationRequestDetailsModal
                request={selected}
                receipts={selected ? receiptsByRequestId?.get(selected.id) : undefined}
                onClose={handleCloseModal}
                currentUserEmail={user?.email}
                isDeleting={deleteRequest.isPending}
                onDeleteRequest={(requestId) => {
                    if (!selected) return;
                    deleteRequest.mutate(
                        {
                            requestId,
                            collaboratorId: selected.collaboratorId,
                            collaboratorEmail: selected.collaboratorEmail,
                            reservedDays: resolveReservedDays(selected),
                        },
                        { onSuccess: handleCloseModal }
                    );
                }}
                isConfirmingRequest={confirmRequest?.isPending ?? false}
                onConfirmRequest={(requestId) => {
                    if (!selected) return;
                    confirmRequest?.mutate({
                        requestId,
                        collaboratorEmail: selected.collaboratorEmail,
                        collaboratorId: selected.collaboratorId,
                    });
                }}
                resendingEmail={
                    resendClientEmail?.isPending ? resendClientEmail.variables?.email ?? null : null
                }
                onResendClientEmail={(requestId, email) => {
                    resendClientEmail?.mutate({ requestId, email });
                }}
                isSubmittingCorrection={submitCorrection?.isPending ?? false}
                onSubmitCorrection={async (requestId, corrections) => {
                    if (!selected || !user?.email || !selected.collaboratorId) return Promise.reject();
                    return new Promise<void>((resolve, reject) => {
                        submitCorrection?.mutate(
                            {
                                requestId,
                                collaboratorEmail: selected.collaboratorEmail,
                                collaboratorId: selected.collaboratorId,
                                corrections,
                            },
                            {
                                onSuccess: () => resolve(),
                                onError: reject,
                            }
                        );
                    });
                }}
            />
        </>
    );
}

export default VacationRequestList;
